import { useState, FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../lib/cartStore";
import { formatCents, calculateEntryTotal } from "../lib/products";
import { submitCart, postToUniMarket } from "../lib/api";

interface FieldErrors {
  adminFirstName?: string;
  adminLastName?: string;
  adminEmail?: string;
}

export function FinalisePage() {
  const navigate = useNavigate();
  const { entries, isEmpty, getCartItems, getTotals } = useCart();

  const [adminFirstName, setAdminFirstName] = useState("");
  const [adminLastName, setAdminLastName] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Redirect back if no cart items
  if (isEmpty) {
    navigate("/punchout/start", { replace: true });
    return null;
  }

  const totals = getTotals();

  const validateField = (field: string, value: string): string | undefined => {
    switch (field) {
      case "adminFirstName":
        return value.trim() ? undefined : "First name is required";
      case "adminLastName":
        return value.trim() ? undefined : "Last name is required";
      case "adminEmail":
        if (!value.trim()) return "Email is required";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return "Please enter a valid email";
        return undefined;
      default:
        return undefined;
    }
  };

  const handleBlur = (field: string, value: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const error = validateField(field, value);
    setFieldErrors((prev) => ({ ...prev, [field]: error }));
  };

  const handleChange = (field: string, value: string, setter: (v: string) => void) => {
    setter(value);
    if (touched[field]) {
      const error = validateField(field, value);
      setFieldErrors((prev) => ({ ...prev, [field]: error }));
    }
  };

  const validateForm = (): boolean => {
    const errors: FieldErrors = {
      adminFirstName: validateField("adminFirstName", adminFirstName),
      adminLastName: validateField("adminLastName", adminLastName),
      adminEmail: validateField("adminEmail", adminEmail),
    };

    setFieldErrors(errors);
    setTouched({ adminFirstName: true, adminLastName: true, adminEmail: true });

    return !errors.adminFirstName && !errors.adminLastName && !errors.adminEmail;
  };

  const getFieldClass = (field: string) => {
    if (touched[field] && fieldErrors[field as keyof FieldErrors]) {
      return "form-group has-error";
    }
    return "form-group";
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!validateForm()) {
      const firstError = document.querySelector(".form-group.has-error");
      firstError?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    setSubmitting(true);

    try {
      const items = getCartItems();
      const { targetUrl, cxmlPayload } = await submitCart(items, {
        adminFirstName: adminFirstName.trim(),
        adminLastName: adminLastName.trim(),
        adminEmail: adminEmail.trim(),
      });
      postToUniMarket(targetUrl, cxmlPayload);
    } catch (err) {
      console.error("Submit failed", err);
      setSubmitError("Failed to submit cart. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <div className="order-page">
      <header className="order-header">
        <img src="https://smartered.wpengine.com/wp-content/uploads/2023/01/SmarterEd-logo.svg" alt="SmarterEd" className="order-header-logo" />
        <div className="order-header-text">
          <h1>Finalise</h1>
        </div>
      </header>

      {submitError && <div className="error-banner">{submitError}</div>}

      <div className="checkout-layout">
        <main className="checkout-form-section">
          <form onSubmit={handleSubmit} noValidate>
            {/* Teacher Licence Administrator */}
            <section className="checkout-card">
              <h2 className="checkout-card-title">Teacher Licence Administrator</h2>
              <p className="checkout-card-description">
                Specify an initial teacher who will manage teacher licences for
                your school. This person will be able to assign and unassign
                teachers to courses. Also note that any teacher assigned to a
                course will then be able to administer other teacher licences.
              </p>

              <div className="form-row">
                <div className={getFieldClass("adminFirstName")}>
                  <label htmlFor="adminFirstName">First name *</label>
                  <input
                    id="adminFirstName"
                    type="text"
                    value={adminFirstName}
                    onChange={(e) => handleChange("adminFirstName", e.target.value, setAdminFirstName)}
                    onBlur={() => handleBlur("adminFirstName", adminFirstName)}
                    aria-invalid={!!fieldErrors.adminFirstName}
                  />
                  {touched.adminFirstName && fieldErrors.adminFirstName && (
                    <span className="field-error">{fieldErrors.adminFirstName}</span>
                  )}
                </div>
                <div className={getFieldClass("adminLastName")}>
                  <label htmlFor="adminLastName">Last name *</label>
                  <input
                    id="adminLastName"
                    type="text"
                    value={adminLastName}
                    onChange={(e) => handleChange("adminLastName", e.target.value, setAdminLastName)}
                    onBlur={() => handleBlur("adminLastName", adminLastName)}
                    aria-invalid={!!fieldErrors.adminLastName}
                  />
                  {touched.adminLastName && fieldErrors.adminLastName && (
                    <span className="field-error">{fieldErrors.adminLastName}</span>
                  )}
                </div>
              </div>

              <div className={getFieldClass("adminEmail")}>
                <label htmlFor="adminEmail">Email *</label>
                <input
                  id="adminEmail"
                  type="email"
                  autoComplete="email"
                  placeholder="admin.teacher@school.edu.au"
                  value={adminEmail}
                  onChange={(e) => handleChange("adminEmail", e.target.value, setAdminEmail)}
                  onBlur={() => handleBlur("adminEmail", adminEmail)}
                  aria-invalid={!!fieldErrors.adminEmail}
                />
                {touched.adminEmail && fieldErrors.adminEmail && (
                  <span className="field-error">{fieldErrors.adminEmail}</span>
                )}
              </div>

              <p className="field-hint">
                This person will receive instructions on how to manage teacher licences.
              </p>
            </section>

            <button type="submit" className="btn btn-primary btn-full btn-lg" disabled={submitting}>
              {submitting ? "Submitting..." : "Return to Marketplace"}
            </button>
          </form>
        </main>

        {/* Order Summary */}
        <aside className="checkout-summary-section">
          <div className="checkout-summary">
            <h2 className="checkout-summary-title">Order Summary</h2>

            {(() => {
              const allItems = getCartItems();
              const discountItems = allItems.filter((item) => item.isDiscount);
              const hasDiscounts = discountItems.length > 0;

              const subtotalBeforeDiscounts = entries.reduce(
                (sum, entry) => sum + calculateEntryTotal(entry.group, entry.quantity, entry.variantId, entry.isAddingToExisting),
                0
              );

              return (
                <>
                  <div className="checkout-summary-items">
                    {entries.map((entry) => {
                      const lineTotal = calculateEntryTotal(entry.group, entry.quantity, entry.variantId, entry.isAddingToExisting);
                      const isStudentProduct = entry.group.productType === "student";
                      const unitLabel = isStudentProduct ? "block" : "teacher";
                      const unitsLabel = isStudentProduct ? "blocks" : "teachers";

                      return (
                        <div key={entry.groupId} className="checkout-summary-item">
                          <div className="checkout-summary-item-info">
                            <div className="checkout-summary-item-name">
                              {entry.group.name}
                              {entry.variantId && (
                                <span className="checkout-summary-item-variant">
                                  {" "}&mdash; {entry.variantId.charAt(0).toUpperCase() + entry.variantId.slice(1)}
                                </span>
                              )}
                            </div>
                            <div className="checkout-summary-item-detail">
                              {entry.quantity} {entry.quantity === 1 ? unitLabel : unitsLabel}
                            </div>
                          </div>
                          <div className="checkout-summary-item-price">
                            {formatCents(lineTotal)}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {hasDiscounts && (
                    <div className="checkout-summary-subtotal-row">
                      <span>Subtotal</span>
                      <span>{formatCents(subtotalBeforeDiscounts)}</span>
                    </div>
                  )}

                  {hasDiscounts && (
                    <div className="checkout-summary-discounts">
                      <div className="checkout-summary-discounts-header">Bundle Discounts</div>
                      {discountItems.map((discount, idx) => (
                        <div key={idx} className="checkout-summary-discount-row">
                          <span className="checkout-summary-discount-name">{discount.name}</span>
                          <span className="checkout-summary-discount-amount">{formatCents(discount.unitPriceExGstCents)}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="checkout-summary-totals">
                    {!hasDiscounts && (
                      <div className="checkout-summary-row">
                        <span>Subtotal</span>
                        <span>{formatCents(totals.subtotalExGstCents)}</span>
                      </div>
                    )}
                    <div className="checkout-summary-row">
                      <span>GST (10%)</span>
                      <span>{formatCents(totals.gstCents)}</span>
                    </div>
                    <div className="checkout-summary-row checkout-summary-total">
                      <span>Total</span>
                      <span>{formatCents(totals.totalIncGstCents)}</span>
                    </div>
                  </div>
                </>
              );
            })()}

            <button
              type="button"
              className="btn btn-secondary btn-full"
              onClick={() => navigate("/punchout/start")}
              disabled={submitting}
            >
              Edit order
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
}
